import React, { useState, useEffect } from 'react';
import AppLayout from '../components/AppLayout';
import { Users, Shield, Plus, X, UserPlus, Mail, Trash2, Clock } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Common timezones
const TIMEZONES = [
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)' },
  { value: 'Europe/Zurich', label: 'Zurich (CET/CEST)' },
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
  { value: 'America/Denver', label: 'Denver (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
];

const UsersManagement = () => {
  const { isDark } = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', name: '', role: 'member' });
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: !currentStatus })
      });

      if (response.ok) {
        fetchUsers();
      }
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  const changeUserRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        fetchUsers();
      } else {
        alert('Failed to update user role');
      }
    } catch (error) {
      console.error('Failed to update user role:', error);
      alert('Failed to update user role');
    }
  };

  const changeUserTimezone = async (userId, newTimezone) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ timezone: newTimezone })
      });

      if (response.ok) {
        fetchUsers();
      } else {
        alert('Failed to update user timezone');
      }
    } catch (error) {
      console.error('Failed to update user timezone:', error);
      alert('Failed to update user timezone');
    }
  };

  const inviteUser = async (e) => {
    e.preventDefault();
    setInviting(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(inviteData)
      });

      if (response.ok) {
        alert(`Invitation sent to ${inviteData.email}. They can sign in using Google.`);
        setShowInviteModal(false);
        setInviteData({ email: '', name: '', role: 'member' });
        fetchUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to invite user');
      }
    } catch (error) {
      console.error('Failed to invite user:', error);
      alert('Failed to invite user');
    } finally {
      setInviting(false);
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to remove ${userName} from the system?`)) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        fetchUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  if (loading) {
    return (
      <AppLayout title="Users Management">
        <div className="flex items-center justify-center h-64">
          <div className="text-[#A2FF00] text-xl">Loading...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Users Management">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Users
            </h1>
            <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Manage team members and their permissions
            </p>
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold rounded-sm transition-colors`}
          >
            <UserPlus className="w-5 h-5" />
            Invite User
          </button>
        </div>

        {/* Users Table */}
        <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm overflow-hidden`}>
          {users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>No users found</h3>
              <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Invite team members to get started</p>
              <button
                onClick={() => setShowInviteModal(true)}
                className={`px-4 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold rounded-sm transition-colors`}
              >
                Invite User
              </button>
            </div>
          ) : (
            <table className="w-full" data-testid="users-table">
              <thead className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <tr>
                  <th className={`text-left p-4 ${isDark ? 'text-gray-400' : 'text-gray-500'} font-medium text-sm`}>User</th>
                  <th className={`text-left p-4 ${isDark ? 'text-gray-400' : 'text-gray-500'} font-medium text-sm`}>Title</th>
                  <th className={`text-left p-4 ${isDark ? 'text-gray-400' : 'text-gray-500'} font-medium text-sm`}>Role</th>
                  <th className={`text-left p-4 ${isDark ? 'text-gray-400' : 'text-gray-500'} font-medium text-sm`}>Timezone</th>
                  <th className={`text-left p-4 ${isDark ? 'text-gray-400' : 'text-gray-500'} font-medium text-sm`}>Status</th>
                  <th className={`text-left p-4 ${isDark ? 'text-gray-400' : 'text-gray-500'} font-medium text-sm`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.user_id} className={`border-b ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50'}`} data-testid={`user-${user.user_id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        {user.picture ? (
                          <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className={`w-10 h-10 rounded-full ${isDark ? 'bg-[#A2FF00]/20' : 'bg-green-100'} flex items-center justify-center`}>
                            <Users className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
                          </div>
                        )}
                        <div>
                          <div className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>{user.name}</div>
                          <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {user.job_title || '-'}
                      </span>
                    </td>
                    <td className="p-4">
                      <select
                        value={user.role}
                        onChange={(e) => changeUserRole(user.user_id, e.target.value)}
                        data-testid={`role-select-${user.user_id}`}
                        className={`${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm px-3 py-1 text-sm focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00]`}
                      >
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td className="p-4">
                      <select
                        value={user.timezone || 'Europe/London'}
                        onChange={(e) => changeUserTimezone(user.user_id, e.target.value)}
                        data-testid={`timezone-select-${user.user_id}`}
                        className={`${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm px-2 py-1 text-sm focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00]`}
                      >
                        {TIMEZONES.map(tz => (
                          <option key={tz.value} value={tz.value}>{tz.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex px-2 py-1 rounded text-xs ${
                        user.is_active
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleUserStatus(user.user_id, user.is_active)}
                          data-testid={`toggle-user-${user.user_id}`}
                          className={`text-sm ${isDark ? 'text-[#A2FF00] hover:text-[#8EFF00]' : 'text-green-600 hover:text-green-700'} transition-colors`}
                        >
                          {user.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => deleteUser(user.user_id, user.name)}
                          className="p-1 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                          title="Remove user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm w-full max-w-md`}>
            <div className={`p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'} flex items-center justify-between`}>
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Invite User
              </h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className={`p-1 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={inviteUser} className="p-4 space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Email Address *
                </label>
                <input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                  required
                  placeholder="user@example.com"
                  className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-[#A2FF00] outline-none`}
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Name *
                </label>
                <input
                  type="text"
                  value={inviteData.name}
                  onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                  required
                  placeholder="John Smith"
                  className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-[#A2FF00] outline-none`}
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Role
                </label>
                <select
                  value={inviteData.role}
                  onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
                  className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-[#A2FF00] outline-none`}
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              
              <div className={`p-3 rounded ${isDark ? 'bg-[#A2FF00]/10' : 'bg-green-50'}`}>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  <Mail className="w-4 h-4 inline mr-2" />
                  The user will be pre-registered and can sign in using Google OAuth with this email address.
                </p>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className={`flex-1 px-4 py-2 ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} rounded-sm transition-colors`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviting}
                  className={`flex-1 px-4 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold rounded-sm transition-colors disabled:opacity-50`}
                >
                  {inviting ? 'Inviting...' : 'Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default UsersManagement;
